/**
 * AI post-processing of a dictation final transcript.
 *
 * Sends the Deepgram-finalised + dictionary-replaced transcript to an
 * OpenAI-compatible chat completions endpoint (Groq by default) and asks
 * the model to clean up punctuation + capitalization + minor transcription
 * slips. This is OFF by default; opt-in via Settings > Dictation > "AI
 * punctuation cleanup".
 *
 * Design decisions:
 *   - Only runs on FINAL transcripts. Interim transcripts fire 10x/sec;
 *     burning the free-tier RPM there would waste the budget and the
 *     user doesn't see interim text in the editor anyway (per the
 *     reliability fix we shipped).
 *   - Hard 2000ms timeout. If the LLM hangs we fall back to the raw
 *     dictionary-replaced text so dictation keeps working.
 *   - Prompt is minimal: punctuate + capitalize, preserve every word
 *     verbatim, return ONLY the cleaned text. The model doesn't need
 *     to "think" — any 70B+ model handles this trivially.
 *   - Any error → return the input unchanged. A single flaky LLM call
 *     must not kill the dictation flow.
 */

/** Sentinel used to protect real newlines from the LLM. The LLM treats
 *  "\n" as whitespace it can "clean up"; we swap newlines to this
 *  low-probability glyph before sending, then swap them back after. The
 *  character chosen is PILCROW (U+00B6) with a surrounding bracket pair
 *  so it's visually distinct AND unlikely to appear in medical text. */
const NEWLINE_SENTINEL = '[¶NL¶]';
const PARAGRAPH_SENTINEL = '[¶PP¶]';

const SYSTEM_PROMPT = `You are a dictation cleanup assistant for medical reports.

Rewrite the user's text so that:
- Punctuation is correct (periods, commas, colons, semicolons, question marks, exclamation marks).
- The first letter of each sentence is capitalized.
- Proper nouns and acronyms stay capitalized.
- Spelling of common medical / radiology terms is corrected if obviously mis-transcribed (e.g. "hyper intense" -> "hyperintense").
- Numbers stay as digits if they were already digits.

Preserve EXACTLY:
- Every occurrence of the token [¶NL¶] (represents a line break the user explicitly dictated).
- Every occurrence of the token [¶PP¶] (represents a paragraph break the user explicitly dictated).
- The number of such tokens and their position in the text — do NOT remove or move them.

Do NOT:
- Add new content or paraphrase.
- Translate.
- Expand or contract abbreviations.
- Change the order of ideas.
- Add any preamble, apology, or commentary.
- Remove, replace, add, or move the [¶NL¶] / [¶PP¶] tokens.

Return ONLY the cleaned text with no quotes and no markdown.`;

export interface PostProcessConfig {
  enabled: boolean;
  endpoint: string;
  model: string;
  apiKey: string;
}

/**
 * Call the configured LLM to clean up a transcript. Returns the cleaned
 * text, or the original input on any failure (timeout, bad key, network,
 * malformed response). Never throws.
 */
export async function postProcessTranscript(
  input: string,
  cfg: PostProcessConfig,
  timeoutMs = 2000,
): Promise<string> {
  if (!cfg.enabled || !cfg.apiKey || !cfg.endpoint || !cfg.model) return input;
  const text = input.trim();
  if (!text) return input;

  // Encode explicit newlines as sentinels. LLMs otherwise "clean up"
  // what they treat as spurious whitespace and the user's "next line"
  // / "new paragraph" commands get silently dropped.
  const encoded = text
    .replace(/\n\n+/g, PARAGRAPH_SENTINEL)
    .replace(/\n/g, NEWLINE_SENTINEL);

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const startedAt = performance.now();

  try {
    const resp = await fetch(cfg.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${cfg.apiKey}`,
      },
      body: JSON.stringify({
        model: cfg.model,
        temperature: 0,
        max_tokens: 400,
        messages: [
          { role: 'system', content: SYSTEM_PROMPT },
          { role: 'user', content: encoded },
        ],
      }),
      signal: controller.signal,
    });

    if (!resp.ok) {
      console.warn(
        '[Dictate] post-process HTTP',
        resp.status,
        '-',
        await resp.text().catch(() => '(no body)'),
      );
      return input;
    }

    const body = (await resp.json()) as {
      choices?: Array<{
        message?: {
          content?: string;
          // gpt-oss family sometimes puts the answer in reasoning_content
          // when it decides the request was simple enough to answer "directly"
          // (empty content). Standard OpenAI SDK clients miss this.
          reasoning_content?: string;
        };
        finish_reason?: string;
      }>;
    };
    const msg = body?.choices?.[0]?.message;
    let rawReply = msg?.content?.trim() || '';
    if (!rawReply && msg?.reasoning_content) {
      // Fall back to reasoning_content. Strip any <think>…</think> wrappers
      // some open-weights models emit around their reasoning.
      rawReply = msg.reasoning_content
        .replace(/<think>[\s\S]*?<\/think>/g, '')
        .trim();
    }
    const elapsed = Math.round(performance.now() - startedAt);
    if (!rawReply) {
      console.warn(
        '[Dictate] post-process returned empty response (both content + reasoning_content). finish_reason=',
        body?.choices?.[0]?.finish_reason,
        'full:',
        JSON.stringify(body).slice(0, 500),
      );
      return input;
    }
    // Decode sentinels back to real newlines so the editor's split('\n')
    // + onEnter() path produces real paragraph breaks.
    const cleaned = rawReply
      .replaceAll(PARAGRAPH_SENTINEL, '\n\n')
      .replaceAll(NEWLINE_SENTINEL, '\n');
    console.log(
      `[Dictate] post-process ${elapsed}ms: ${JSON.stringify(input)} -> ${JSON.stringify(cleaned)}`,
    );
    return cleaned;
  } catch (err) {
    const elapsed = Math.round(performance.now() - startedAt);
    const name = err instanceof Error ? err.name : 'unknown';
    console.warn(`[Dictate] post-process failed after ${elapsed}ms (${name}); using uncleaned text`);
    return input;
  } finally {
    clearTimeout(timer);
  }
}
